import { QUESTION_TYPES } from '@api/instant_survey';

import {
  ALL_OTHERS_LABEL,
  ALL_OTHERS_VALUE,
  CUT_ELIGIBLE_QUESTION_TYPES,
  DEFAULT_RANK_TOP_N,
  GROUPED_DEMOGRAPHIC_PRESETS,
  OVERALL_SOURCE_ID,
  QUOTA_GROUP_SOURCE_ID,
  RANK_QUESTION_TYPES,
  ROW_ELIGIBLE_QUESTION_TYPES,
  SOURCE_CATEGORIES,
  TRIP_ATTRIBUTE_HEADERS,
  VALUE_PICKER_THRESHOLD,
} from './constants';

let idCounter = 0;
export const createId = (prefix = 'ct') => {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
};

export const getQuestionLabel = ({ index, title }) => `Q${index + 1}. ${title}`;

/** Instructional-text entries are dropped and don't advance the question number. */
const getNumberedQuestions = (surveyQuestions = []) =>
  surveyQuestions
    .filter(({ type }) => type !== QUESTION_TYPES.INSTRUCTIONS.value)
    .map((question, index) => ({ ...question, index }));

export const getEligibleQuestions = (surveyQuestions = []) =>
  getNumberedQuestions(surveyQuestions).filter(({ type }) =>
    ROW_ELIGIBLE_QUESTION_TYPES.includes(type)
  );

export const getIneligibleQuestions = (surveyQuestions = []) =>
  getNumberedQuestions(surveyQuestions).filter(
    ({ type }) => !ROW_ELIGIBLE_QUESTION_TYPES.includes(type)
  );

/** Grid questions keep their shared answer scale in `options`, separate from `subQuestions`. */
export const getQuestionOptionValues = (question = {}) =>
  (question.options ?? []).map(({ id, title }) => ({
    id: String(id),
    label: title,
  }));

export const isRankQuestion = ({ type } = {}) => RANK_QUESTION_TYPES.includes(type);

export const getDefaultRankTopN = (question = {}) =>
  Math.min(DEFAULT_RANK_TOP_N, Math.max(getQuestionOptionValues(question).length, 1));

const getDemographicOptionValues = (options = []) =>
  options.map(({ id, label }) => ({
    id: String(id),
    label: label ?? String(id),
  }));

/** Sorts distinct raw-data values so picker order is stable. */
const getTripAttributeValues = (rows, accessor) => {
  const distinct = new Set();
  rows.forEach((row) => {
    const value = row?.[accessor];
    if (value !== null && value !== undefined && value !== '') distinct.add(String(value));
  });
  return [...distinct].sort().map((value) => ({ id: value, label: value }));
};

const buildGroupedPresetSource = (demographicFilter) => {
  const preset = GROUPED_DEMOGRAPHIC_PRESETS[demographicFilter.id];
  if (!preset) return null;

  const availableIds = new Set((demographicFilter.options ?? []).map(({ id }) => String(id)));
  const bins = preset.bins.filter(({ values }) => values.some((value) => availableIds.has(value)));
  if (!bins.length) return null;

  return {
    category: SOURCE_CATEGORIES.DEMOGRAPHIC,
    id: `${demographicFilter.id}__grouped`,
    isGroupedPreset: true,
    label: preset.label,
    presetBins: bins,
    sourceAttribute: demographicFilter.id,
    values: [],
  };
};

const OVERALL_SOURCE = {
  category: SOURCE_CATEGORIES.OVERALL,
  id: OVERALL_SOURCE_ID,
  label: 'Overall',
  sourceAttribute: OVERALL_SOURCE_ID,
  values: [{ id: 'total', label: 'Total' }],
};

/** Preserves programmed question order and standard demographic order instead of count-sorting. */
export const buildBannerSources = ({ globalFilters, rawData, surveyQuestions } = {}) => {
  const sources = [OVERALL_SOURCE];

  if (globalFilters?.quotaGroups?.length > 1) {
    sources.push({
      category: SOURCE_CATEGORIES.QUOTA_GROUP,
      id: QUOTA_GROUP_SOURCE_ID,
      label: 'Quota Group',
      sourceAttribute: QUOTA_GROUP_SOURCE_ID,
      values: globalFilters.quotaGroups.map(({ instantSurveyGroupRef, name }) => ({
        id: String(instantSurveyGroupRef),
        label: name,
      })),
    });
  }

  (globalFilters?.demographicFilters ?? []).forEach((filter) => {
    sources.push({
      category: SOURCE_CATEGORIES.DEMOGRAPHIC,
      id: filter.id,
      label: filter.label,
      sourceAttribute: filter.id,
      values: getDemographicOptionValues(filter.options),
    });

    const groupedSource = buildGroupedPresetSource(filter);
    if (groupedSource) sources.push(groupedSource);
  });

  const rawTable = rawData?.[0]?.table;
  if (rawTable?.columns?.length && rawTable?.data?.length) {
    TRIP_ATTRIBUTE_HEADERS.forEach((header) => {
      const column = rawTable.columns.find(({ Header }) => Header === header);
      if (!column) return;

      const values = getTripAttributeValues(rawTable.data, column.accessor);
      if (!values.length) return;

      sources.push({
        category: SOURCE_CATEGORIES.TRIP_ATTRIBUTE,
        id: `trip__${column.accessor}`,
        label: header,
        sourceAttribute: header,
        values,
      });
    });
  }

  getEligibleQuestions(surveyQuestions)
    .filter(({ type }) => CUT_ELIGIBLE_QUESTION_TYPES.includes(type))
    .forEach((question) => {
      const values = getQuestionOptionValues(question);
      if (!values.length) return;

      sources.push({
        category: SOURCE_CATEGORIES.QUESTION,
        id: `question__${question.id}`,
        isMultiSelectCut: question.type === 'CHECKBOX',
        label: getQuestionLabel(question),
        sourceAttribute: String(question.id),
        values,
      });
    });

  return sources;
};

export const sourceNeedsValuePicker = (source) =>
  !source.isGroupedPreset && source.values.length > VALUE_PICKER_THRESHOLD;

/**
 * Create a banner span from a source. For picker sources, `selectedValues` holds the
 * chosen values; low-cardinality sources drop all their values in as chips directly.
 */
export const createSpanFromSource = (source, { selectedValues, withAllOthers } = {}) => {
  let points;

  if (source.isGroupedPreset) {
    // Preserve preset bins as indivisible net members.
    points = source.presetBins.map((bin) => ({
      atomic: true,
      key: createId('point'),
      label: bin.label,
      values: [...bin.values],
    }));
  } else {
    const values = selectedValues ?? source.values;
    points = values.map(({ id, label }) => ({
      key: createId('point'),
      label,
      values: [id],
    }));
    if (withAllOthers) {
      points.push({
        isAllOthers: true,
        key: createId('point'),
        label: ALL_OTHERS_LABEL,
        values: [ALL_OTHERS_VALUE],
      });
    }
  }

  return {
    category: source.category,
    id: createId('span'),
    isMultiSelectCut: Boolean(source.isMultiSelectCut),
    label: source.label,
    points,
    sourceAttribute: source.sourceAttribute,
    sourceId: source.id,
  };
};

export const createOverallSpan = () => createSpanFromSource(OVERALL_SOURCE);

export const getDefaultNetName = (points = []) => points.map(({ label }) => label).join(' & ');

/**
 * Combine two or more points of a span into a named net. Member points are kept so
 * a net can be dissolved back to them (grouped bins reappear as bins, not raw values).
 */
export const combineSpanPoints = (span, pointKeys, name) => {
  const members = span.points.filter(({ key }) => pointKeys.includes(key));
  // "All Others" is the complement of the span's explicit values, not a value
  // itself - folding it into a net would export its sentinel as a literal value.
  if (members.length < 2 || members.some(({ isAllOthers }) => isAllOthers)) return span;

  const net = {
    isNet: true,
    key: createId('point'),
    label: name?.trim() || getDefaultNetName(members),
    memberPoints: members,
    values: members.flatMap(({ values }) => values),
  };
  const firstIdx = span.points.findIndex(({ key }) => pointKeys.includes(key));
  const remaining = span.points.filter(({ key }) => !pointKeys.includes(key));
  remaining.splice(firstIdx, 0, net);

  return { ...span, points: remaining };
};

export const dissolveSpanPoint = (span, pointKey) => {
  const index = span.points.findIndex(({ key }) => key === pointKey);
  const point = span.points[index];
  if (!point?.isNet || !point.memberPoints?.length) return span;

  const points = [...span.points];
  points.splice(index, 1, ...point.memberPoints);
  return { ...span, points };
};

export const renameSpanPoint = (span, pointKey, name) => ({
  ...span,
  points: span.points.map((point) =>
    point.key === pointKey && name?.trim() ? { ...point, label: name.trim() } : point
  ),
});

export const removeSpanPoint = (span, pointKey) => ({
  ...span,
  points: span.points.filter(({ key }) => key !== pointKey),
});

export const countBannerColumns = (banner = []) =>
  banner.reduce((total, { points }) => total + points.length, 0);

export const buildDefaultQuestionConfigs = (surveyQuestions = []) =>
  getEligibleQuestions(surveyQuestions).map((question) => ({
    included: true,
    nets: [],
    questionId: String(question.id),
    ...(isRankQuestion(question) && { rankTopN: getDefaultRankTopN(question) }),
  }));

const buildPointPayload = (point) => ({
  is_all_others: Boolean(point.isAllOthers),
  label: point.label,
  values: point.isAllOthers ? [] : point.values,
});

export const buildExportPayload = ({
  banner = [],
  baseThreshold,
  confidence,
  filterLogic,
  filterRows = [],
  filterSources = [],
  questionConfigs = [],
}) => ({
  banner: banner.map((span) => ({
    is_multi_select_cut: Boolean(span.isMultiSelectCut),
    label: span.label,
    points: span.points.map(buildPointPayload),
    source_id: span.sourceAttribute,
    source_type: span.category,
  })),
  base_threshold: baseThreshold,
  confidence,
  filters: {
    logic: filterLogic,
    rows: filterRows
      .filter(({ sourceId, values }) => sourceId && values.length)
      // Rows echo the banner-span contract so the backend resolves both the same way.
      .map(({ sourceId, values }) => {
        const source = filterSources.find(({ id }) => id === sourceId);
        return {
          source_id: source?.sourceAttribute ?? sourceId,
          source_type: source?.category ?? null,
          values,
        };
      }),
  },
  questions: questionConfigs.map(({ included, nets, questionId, rankTopN }) => ({
    included,
    nets: nets.map(({ name, optionIds }) => ({
      name,
      option_ids: optionIds,
    })),
    question_id: questionId,
    ...(rankTopN !== undefined && { rank_top_n: rankTopN }),
  })),
});
